import React, { useCallback, useLayoutEffect } from 'react';
import Animated from 'react-native-reanimated';
import { View } from 'react-native';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { CardView } from 'design-system/CardView';
import IconArrow from 'design-system/icons/IconArrow';
import Logo from 'design-system/Logo';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { rem, AppView, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { formatAmount } from 'utils/formatting';
import { selectSellablePositionsForTradingAccount, selectTradingAccount } from '../reducers/selectors';
import { getTradingAccountName } from '../utils';
import useStockMeta from '../hooks/useStockMeta';
import { isWeb } from '../../../constants';
const strings = {
    title: 'Select a fund or ETF to sell',
    subTitle: 'Pick one of your current investments to sell',
};
const SellablePositionRow = ({ position, onPress, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const stockMeta = useStockMeta(position.assetId);
    const handlePress = useCallback(() => onPress(position.assetId), [onPress, position.assetId]);
    return (<TouchableHighlight hoverOnLight underlayColor={colors.background.underlay} activeOpacity={1} onPress={handlePress}>
      <View style={styles.row}>
        <Logo R44 uri={stockMeta?.iconUrl || position.iconUrl}/>
        <Spacer w={16}/>
        <View style={styles.name}>
          <Text Text-16>{stockMeta?.name || position.name}</Text>
          <Text TextThin-14 Secondary>
            {`Current value ${formatAmount(position.amount.value, position.amount.currency, 2)}`}
          </Text>
        </View>
        <Spacer w={16}/>
        <IconArrow />
      </View>
    </TouchableHighlight>);
};
const SelectAssetToSellScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const colors = useColors();
    const { sellType } = route.params;
    const positions = useAppSelector(selectSellablePositionsForTradingAccount);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', 0, paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.elements.primary,
            headerRight: () => <HelpScreenButton />,
        });
    }, [colors.elements.primary]);
    const onSelectAsset = useCallback((assetId) => {
        if (sellType === 'switch') {
            navigation.navigate('InvestAssetSwitch', { assetId });
        }
        else {
            navigation.navigate('SellAssetFromTradingAccount', { assetId });
        }
    }, [sellType]);
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <ScreenInfoHeader title={strings.title} subTitle={strings.subTitle} preTitle={getTradingAccountName(tradingAccount?.account)}/>
            <CardView noMarginTop>
              {positions.map((position) => (<SellablePositionRow key={position.assetId} position={position} onPress={onSelectAsset}/>))}
            </CardView>
          </Animated.ScrollView>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets(() => ({
    row: {
        padding: rem(16),
        alignItems: 'center',
        flexDirection: 'row',
    },
    name: {
        flex: 1,
    },
}));
export default SelectAssetToSellScreen;
